import StyledButton from '@/components/styled/StyledButton';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import StyledTooltipContainer from '@/components/styled/StyledTooltipContainer';
import StyledInput from '@/components/styled/StyledInput';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '@/components/styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';

interface CategoryOneWayFeesListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CategoryOneWayFeesList = ({
  control,
  index,
  language,
  isViewMode = false,
}: CategoryOneWayFeesListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `carCategories.${index}.oneWayFees`,
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: `carCategories.${index}.oneWayFees`,
    });
  return (
    <>
      {fields.map((item, oneWayFeeIndex) => (
        <CategoryOneWayFeeCard
          key={item.id}
          control={control}
          categoryIndex={index}
          oneWayFeeIndex={oneWayFeeIndex}
          deleteOneWayFee={() => {
            remove(oneWayFeeIndex);
            removeTranslation(oneWayFeeIndex);
          }}
          language={language}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addOneWayFee')}
          onClick={() => {
            append({
              name: undefined,
              description: undefined,
              amount: undefined,
              billingType: undefined,
            } as CarRentalFormDataWithTranslations['carCategories'][number]['oneWayFees'][number]);
            appendTranslation(() => ({
              name: undefined,
              description: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </>
  );
};

export default CategoryOneWayFeesList;

interface CategoryOneWayFeeCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  categoryIndex: number;
  oneWayFeeIndex: number;
  deleteOneWayFee: () => void;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CategoryOneWayFeeCard = ({
  control,
  categoryIndex,
  oneWayFeeIndex,
  deleteOneWayFee,
  language,
  isViewMode = false,
}: CategoryOneWayFeeCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation =
      translations?.[language]?.carCategories?.[categoryIndex]?.oneWayFees?.[
        oneWayFeeIndex
      ];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.oneWayFee', { index: oneWayFeeIndex + 1 })
    );
  }, [currentTitle, categoryIndex, oneWayFeeIndex, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {oneWayFeeIndex > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => deleteOneWayFee()}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.name`}
            control={control}
            title={t('carRental.categoryForm.name')}
            name={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.namePlaceholder')}
            disabled={isViewMode}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledInput
              control={control}
              title={t('carRental.categoryForm.departureCity')}
              name={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.departureCity`}
              placeholder={t('carRental.categoryForm.departureCityPlaceholder')}
              disabled={isViewMode}
            />
            <StyledInput
              control={control}
              title={t('carRental.categoryForm.arrivalCity')}
              name={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.arrivalCity`}
              placeholder={t('carRental.categoryForm.arrivalCityPlaceholder')}
              disabled={isViewMode}
            />
          </div>
          <StyledTranslatableTextarea
            control={control}
            originalName={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.description`}
            title={t('carRental.categoryForm.oneWayFeeDescription')}
            name={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.description`}
            setValue={setValue}
            language={language}
            placeholder={t(
              'carRental.categoryForm.oneWayFeeDescriptionPlaceholder',
            )}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`carCategories.${categoryIndex}.oneWayFees.${oneWayFeeIndex}.amount`}
                  placeholder={t('carRental.categoryForm.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
